<template>
    <app-layout title="Customer Users">
        <page-header>
            <template #title>Users - {{ customer.name }}</template>

            <template #actions>
                <jet-button @click="showInviteModal = true">Invite User</jet-button>
                <jet-tertiary-button class="leading-none" :href="route('customers.index')">
                    Close
                </jet-tertiary-button>
            </template>
        </page-header>

        <customer-navigation :customer="customer" />

        <div class="px-8">
            <div class="rounded-t overflow-hidden border border-b-0">
                <table class="table w-full">
                    <thead>
                        <tr>
                            <th class="text-left">Name</th>
                            <th class="text-left">Email</th>
                            <th class="text-center">Verified</th>
                            <th class="text-center">Enabled</th>
                            <th class="text-right w-0"></th>
                            <th class="text-right w-0"></th>
                            <th class="text-right w-0"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="user in users.data" :key="user.id">
                            <td>{{ user.name }}</td>
                            <td>{{ user.email }}</td>
                            <td class="text-center">{{ user.email_verified_at ? 'Yes' : 'No' }}</td>
                            <td class="text-center">{{ user.enabled ? 'Yes' : 'No' }}</td>
                            <td class="text-right">
                                <jet-button class="mx-4 whitespace-nowrap" v-if="user.email_verified_at === null" @click="resendInvite(user)">
                                    Resend Invite
                                </jet-button>
                            </td>
                            <td class="text-right">
                                <jet-danger-button class="mx-4" v-if="user.enabled" @click="setUser(user)">
                                    Disable
                                </jet-danger-button>

                                <jet-secondary-button class="mx-4" v-else @click="setUser(user)">
                                    Enable
                                </jet-secondary-button>
                            </td>
                            <td class="text-right">
                                <jet-danger-button class="mx-4" @click="removeUser(user)">
                                    Remove
                                </jet-danger-button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-if="users.data.length" class="px-1 float-right text-center text-xs">*removes from current<br>customer only</div>


            <pagination class="inline-flex" :links="users.links" />

            <jet-confirmation-modal :show="showResendModal" @close="closeResendModal" v-if="selectedUser">
                <template #title>
                    Resend Invitation
                </template>

                <template #content>
                    <p>Are you sure you would like to resend an invitation to <span class="font-bold">{{ selectedUser.name }}</span>?</p>
                </template>

                <template #footer>
                    <jet-danger-button @click="handleResendInvite" :disabled="userStatusForm.processing">
                        Yes
                    </jet-danger-button>

                    <jet-tertiary-button class="ml-2" @click="closeResendModal">
                        No
                    </jet-tertiary-button>
                </template>
            </jet-confirmation-modal>

            <jet-confirmation-modal :show="showConfirmationModal" @close="closeConfirmationModal" v-if="selectedUser">
                <template #title>
                    Disable User Account
                </template>

                <template #content>
                    <p>Are you sure you would like to {{ toggleAction }} <span class="font-bold">{{ selectedUser.name }}'s</span> account?</p>
                </template>

                <template #footer>
                    <jet-danger-button @click="toggleUserStatus" :disabled="userStatusForm.processing" v-if="selectedUser.enabled">
                        Yes
                    </jet-danger-button>

                    <jet-secondary-button @click="toggleUserStatus" :disabled="userStatusForm.processing" v-else>
                        Yes
                    </jet-secondary-button>

                    <jet-tertiary-button class="ml-2" @click="closeConfirmationModal">
                        No
                    </jet-tertiary-button>
                </template>
            </jet-confirmation-modal>

            <jet-confirmation-modal :show="showRemoveModal" @close="closeRemoveModal" v-if="selectedUser">
                <template #title>
                    Remove User From Customer
                </template>

                <template #content>
                    <p>Are you sure you would like to remove <span class="font-bold">{{ selectedUser.name }}</span>
                    from <span class="font-bold">{{ customer.name }}'s</span> account?</p>
                </template>

                <template #footer>
                    <jet-danger-button @click="handleRemoveUser" :disabled="userStatusForm.processing">
                        Yes
                    </jet-danger-button>

                    <jet-tertiary-button class="ml-2" @click="closeRemoveModal">
                        No
                    </jet-tertiary-button>
                </template>
            </jet-confirmation-modal>

            <jet-dialog-modal :show="showInviteModal" @close="closeInviteModal">
                <template #title>
                     <h3 class="text-2xl">Send Email Invitation</h3>
                </template>

                <template #content>
                    <form @submit.prevent="inviteUser" class="my-6">
                        <div class="flex gap-x-4 items-center mb-4">
                            <jet-label value="Name:" class="mb-0" style="width: 60px;" />
                            <jet-input v-model="userInviteForm.name" type="text" />
                            <jet-input-error :message="userInviteForm.errors.name" />
                        </div>

                        <div class="flex gap-x-4 items-center">
                            <jet-label value="Email:" class="mb-0" style="width: 60px;" />
                            <jet-input v-model="userInviteForm.email" type="text" />
                            <jet-input-error :message="userInviteForm.errors.email" />
                        </div>

                        <input type="submit" class="hidden" ref="submitInviteUser" />
                    </form>
                </template>

                <template #footer>
                    <jet-button class="mr-2" @click="$refs.submitInviteUser.click()" :disabled="userInviteForm.processing">
                        Send
                    </jet-button>

                    <jet-tertiary-button @click="closeInviteModal">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-dialog-modal>
        </div>
    </app-layout>
</template>

<script>
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import CustomerNavigation from '@/Shared/CustomerNavigation';
import JetButton from '@/Jetstream/Button';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import Pagination from '@/Jetstream/Pagination';

export default {
    components: {
        AppLayout,
        PageHeader,
        CustomerNavigation,
        JetButton,
        JetSecondaryButton,
        JetTertiaryButton,
        JetDangerButton,
        JetConfirmationModal,
        JetDialogModal,
        JetInput,
        JetInputError,
        JetLabel,
        Pagination,
    },

    props: ['customer', 'users'],

    data() {
        return {
            showConfirmationModal: false,
            showInviteModal: false,
            showRemoveModal: false,
            showResendModal: false,

            userStatusForm: this.$inertia.form(),
            userInviteForm: this.$inertia.form({
                name: '',
                email: '',
            }),

            selectedUser: null,
        }
    },

    computed: {
        toggleAction () {
            if (!this.selectedUser) {
                return null;
            }

            return this.selectedUser.enabled ? 'disable' : 'enable';
        },
    },

    methods: {
        toggleUserStatus () {
            this.userStatusForm.post(this.route('customers.users.toggle', {
                customer: this.customer.id,
                user: this.selectedUser.id,
            }), {
                onSuccess: () => {
                    this.userStatusForm.reset();
                    this.showConfirmationModal = false;
                }
            });
        },

        handleRemoveUser () {
            this.userStatusForm.post(this.route('customers.users.remove', {
                customer: this.customer.id,
                user: this.selectedUser.id,
            }), {
                onSuccess: () => {
                    this.userStatusForm.reset();
                    this.showRemoveModal = false;
                }
            });
        },

        handleResendInvite() {
            this.userStatusForm.post(this.route('customers.users.resend', {
                customer: this.customer.id,
                user: this.selectedUser.id,
            }), {
                onSuccess: () => {
                    this.userStatusForm.reset();
                    this.showResendModal = false;
                }
            });
        },


        inviteUser () {
            this.userInviteForm.post(this.route('customers.users.invite', this.customer.id), {
                onSuccess: () => {
                    this.userInviteForm.reset();
                    this.showInviteModal = false;
                }
            });
        },

        resendInvite(user) {
            this.selectedUser = user;
            this.showResendModal = true;
        },

        setUser (user) {
            this.selectedUser = user;
            this.showConfirmationModal = true;
        },

        removeUser (user) {
            this.selectedUser = user;
            this.showRemoveModal = true;
        },

        closeConfirmationModal () {
            this.selectedUser = null;
            this.showConfirmationModal = false;
        },

        closeInviteModal () {
            this.showInviteModal = false;
            this.userInviteForm.reset();
            this.userInviteForm.clearErrors();
        },

        closeRemoveModal() {
            this.selectedUser = null;
            this.showRemoveModal = false;
        },

        closeResendModal() {
            this.selectedUser = null;
            this.showResendModal = false;
        },
    }
}
</script>

<style scoped>
.table td button {
    min-width: 85px;
}
</style>